import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { Alert, TextInput } from '@src/components';
import InfoTooltip from '@src/components/InfoTooltip';
import ReactSelectInput from '@src/components/ReactSelectInput';
import { getCountriesTranslated } from '@src/constants/countries';
import { useUpdateNifOrderMutation } from '@src/generated/graphql';
import { useStatus } from '@src/hooks/useStatus';
import { logAndFormatError } from '@src/services/error-formatter';
import OrderNavigationButtons from '../../components/OrderNavigationButtons';
import { VALIDATIONS } from '../../utils/validation';
import { NifOrderStepPageProps } from '../types/stepProps';

// t('nif:validation.street_address', 'Enter the street address')
// t('nif:validation.city', 'Enter the city')
// t('nif:validation.postal', 'Enter the postal code')
// t('nif:validation.country', 'Enter the country')

const formSchema = z.object({
  address: z.object({
    id: z.string().optional(),
    lineOne: VALIDATIONS.requiredStringWithoutSpecialChars,
    lineTwo: VALIDATIONS.stringWithoutSpecialChars.default(''),
    city: VALIDATIONS.requiredStringWithoutSpecialChars,
    region: VALIDATIONS.stringWithoutSpecialChars.default(''),
    postalCode: VALIDATIONS.requiredStringWithoutSpecialChars,
    country: VALIDATIONS.country,
  }),
});

type FormData = z.infer<typeof formSchema>;

function NifResidentialAddressPage({
  nifOrder,
  previousUrl,
  getNextUrl,
}: NifOrderStepPageProps): JSX.Element {
  const existingAddress = nifOrder.address;
  const { control, handleSubmit } = useForm<FormData>({
    defaultValues: {
      address: {
        lineOne: existingAddress?.lineOne || '',
        lineTwo: existingAddress?.lineTwo || '',
        city: existingAddress?.city || '',
        region: existingAddress?.region || '',
        postalCode: existingAddress?.postalCode || '',
        country: existingAddress?.country || '',
      },
    },
    resolver: zodResolver(formSchema),
  });
  const [updateNifOrder] = useUpdateNifOrderMutation();
  const { status, setError } = useStatus();
  const navigate = useNavigate();
  const { t, i18n: localI18n } = useTranslation(['nif']);

  function onSubmit(formData: FormData): void {
    updateNifOrder({
      variables: {
        input: {
          id: nifOrder.id,
          data: formData,
        },
      },
    })
      .then((updatedOrder) => {
        const updatedNifOrder = updatedOrder?.data?.updateNifOrder?.nifOrder;
        if (!updatedNifOrder) {
          throw new Error('updateNifOrder returned null');
        }

        void navigate(getNextUrl(updatedNifOrder));
      })
      .catch((err) => setError(logAndFormatError(err)));
  }

  const countryOptions = useMemo(
    () =>
      getCountriesTranslated(localI18n.language).map((c) => ({
        label: c.name,
        value: c.code,
      })),
    [localI18n.language],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <div className="space-y-2">
        <h2>{t('residential_address.title', 'Residential address')}</h2>
        <p>
          {t(
            'residential_address.body',
            'The applicant must have a residential address outside of Portugal. P.O. boxes and business addresses are not accepted.',
          )}
        </p>
      </div>
      <Alert.WithStatus status={status} />
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
        <div className="space-y-8 sm:col-span-2">
          <TextInput.LabelledController
            label={t(
              'residential_address.address_line_1_label',
              'Street address (Line 1)',
            )}
            control={control}
            name="address.lineOne"
          />
          <TextInput.LabelledController
            label={t(
              'residential_address.address_line_2_label',
              'Street address (Line 2)',
            )}
            control={control}
            name="address.lineTwo"
          />
        </div>
        <TextInput.LabelledController
          label={t('residential_address.city_label', 'City / Town')}
          control={control}
          name="address.city"
        />
        <TextInput.LabelledController
          label={
            <div className="flex space-x-2">
              <span>
                {t('residential_address.region_label', 'State or province')}
              </span>
              <InfoTooltip>
                {t(
                  'residential_address.tooltip_region',
                  'This field is optional. Please use this line to ensure that the residential address provided matches your Proof of Address document exactly.',
                )}
              </InfoTooltip>
            </div>
          }
          control={control}
          name="address.region"
        />
        <TextInput.LabelledController
          label={t('residential_address.postal_label', 'Postal code')}
          control={control}
          name="address.postalCode"
        />
        <ReactSelectInput.LabelledController
          label={t('residential_address.country_label', 'Country')}
          options={countryOptions}
          control={control}
          name="address.country"
        />
      </div>
      <OrderNavigationButtons previousUrl={previousUrl} buttonType="submit" />
    </form>
  );
}

export default NifResidentialAddressPage;
